/**
 * Missed-visit path (3.2.3): the EHR's missed status was consumed; rebook inside the
 * ordered week on a working clinician and the pace indicator recovers. The MD
 * notification of record stays in the EHR.
 */
import { useMemo, useState } from "react";
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { evaluateMove } from "../../engine/moveGuard";
import { rosterEntries } from "../../engine/roster";
import { rebookMissed } from "../../presenter/actions";
import { useDemoState } from "../../store/useDemoStore";
import { OnPaceIndicator } from "../episode/OnPaceIndicator";
import { useBranchClock } from "../../components/branchClock";

export function RebookDialog({ patientRef, onClose }: { patientRef: string; onClose: () => void }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const missed = useMemo(() => Object.values(state.planVisits).find((v) => state.episodes[v.episodeId]?.patientRef === patientRef && v.status === "missed"), [state.planVisits, state.episodes, patientRef]);
  const [date, setDate] = useState("");
  const [handle, setHandle] = useState(missed?.providerHandle ?? "");
  const [time, setTime] = useState("15:30");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const ev = useMemo(() => (missed ? evaluateMove(state, missed.id, date || missed.targetDate, handle || undefined) : undefined), [state, missed, date, handle]);
  if (!missed || !ev) {
    return (
      <Dialog open onClose={onClose}>
        <DialogContent>
          <Typography variant="body2">No canceled visit to rebook for this patient.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  const candidates = rosterEntries(state).filter((e) => state.config.visitTypes[missed.visitTypeKind].disciplines.includes(e.discipline));
  const fmt = (d: string) => formatInBranchZone(dateKeyStart(d, tz), tz, "EEE d");
  const clock = useBranchClock(tz);
  const canCommit = !!date && ev.allowed && date !== missed.targetDate;
  const commit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await rebookMissed(patientRef, { date, time, handle: handle || undefined });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rebook missed visit · {state.patients[patientRef]?.displayName}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {state.config.visitTypes[missed.visitTypeKind].label} missed {fmt(missed.targetDate)}. Rebook inside the week {fmt(ev.window.start)} – {fmt(ev.window.end)}.
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {ev.days
              .filter((d) => d.date !== missed.targetDate)
              .map((d) => (
                <Chip key={d.date} label={fmt(d.date)} size="small" color={date === d.date ? "primary" : "default"} variant={date === d.date ? "filled" : "outlined"} disabled={!d.allowed} onClick={() => setDate(d.date)} title={d.reason && clock.sentence(d.reason)} data-day={d.date} />
              ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" select label="Clinician" value={handle} onChange={(e) => setHandle(e.target.value)} sx={{ minWidth: 200 }}>
              {candidates.map((e) => (
                <MenuItem key={e.handle} value={e.handle}>
                  {e.displayName} · {e.discipline}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" type="time" label="Arrival" value={time} onChange={(e) => setTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Pace after the rebook
          </Typography>
          <OnPaceIndicator pace={ev.paceAfter} />
          {!ev.allowed && date && (
            <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
              {ev.reasons.join(" · ")}
            </Alert>
          )}
          {error && (
            <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={commit} disabled={busy || !canCommit}>
          Rebook
        </Button>
      </DialogActions>
    </Dialog>
  );
}
